import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadElementAsPdf, downloadStoredContractPdf } from "../../lib/contractPdf";
import ApiCall from "../../../Apicall/ApiCall";
import ContractDocumentView from "./ContractDocumentView";
import { resolveBuyerName } from "./SellerProfileFlow";

// The mockup's own "document modal" (PDF Viewer Presentation 04,
// DocumentPreviewDialog + index.css .ds-viewer-*): opening a document is not
// a departure, so it opens ABOVE the page on a muted ink scrim — the
// transaction record stays mounted behind it — rather than navigating to a
// separate route. A sticky 56px white header (title, download, close) over
// one scroll surface holding the document, which renders at its own natural
// width on the workspace background instead of being squeezed into a narrow
// form dialog. Renders the exact same ContractDocumentView the buyer and
// seller themselves see on /Contract/:id (ContractAgreementFlow), fetching
// the same ContractMain DTO, so it is unmistakably the same signed contract
// for the same vehicle — never a rebuilt summary of it.
// `viewerRole` ("seller" | "buyer"): the contract opens as that party's own copy.
export default function ContractPreviewModal({ t, open, onClose, id, viewerRole = null }) {
  const [contractData, setContractData] = useState(null);
  const [buyerName, setBuyerName] = useState("");
  const [loading, setLoading] = useState(true);
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const openerRef = useRef(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (!open || !id) return;
    let active = true;
    setLoading(true);
    setContractData(null);
    (async () => {
      try {
        const response = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: id } });
        if (!active) return;
        const result = response?.data?.result;
        if (result) {
          setContractData(result);
          resolveBuyerName(result).then((name) => {
            if (active) setBuyerName(name);
          });
        }
      } catch (err) {
        console.error("Contract preview fetch failed", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, id]);

  // Scroll lock, Escape-to-close and focus handling — same grammar as every
  // other ds dialog, just not built on the generic shadcn Dialog primitive:
  // that one is sized for small forms (max-w-lg), not a document.
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 60);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && opener.isConnected) {
        window.setTimeout(() => opener.focus(), 0);
      }
    };
  }, [open, onClose]);

  const trapTab = (event) => {
    if (event.key !== "Tab") return;
    const root = modalRef.current;
    if (!root) return;
    const focusable = Array.from(root.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')).filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Downloads exactly what's on screen — the same ContractDocumentView this
  // modal renders, printed straight to PDF via the browser's own print
  // pipeline (react-to-print, the same mechanism ContractAgreementFlow's own
  // "Print" button already uses) — rather than a separate server-rendered
  // PDF, which has its own template and would drift from whatever this view
  // shows.
  const { i18n } = useTranslation();
  const handleDownload = async () => {
    if (!printRef.current) return;
    try {
      // Once anyone has signed, download the document Klargo preserved for signing
      // (the exact bytes the signatures cover), not a re-render of current data.
      if (contractData?.dealComplete || contractData?.sellerSignedAt || contractData?.buyerSignedAt) {
        await downloadStoredContractPdf({ id: contractData?.id || id, lang: i18n.language?.startsWith("sv") ? "sv" : "en" });
        return;
      }
      await downloadElementAsPdf(printRef.current, `Klargo-Contract-${contractData?.carInfoRegistrationNo || id}.pdf`);
    } catch (err) {
      console.error("PDF download failed", err);
    }
  };

  if (!open) return null;

  return (
    <div className="ds-scope ds-animate-veil ds-viewer-scrim fixed inset-0 z-[80] flex" data-testid="document-viewer">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("dealDetails.doc.contract")}
        ref={modalRef}
        onKeyDown={trapTab}
        data-testid="document-preview"
        className="ds-viewer-modal ds-animate-layer"
      >
        <div data-testid="viewer-toolbar" className="ds-viewer-modal-header flex shrink-0 items-center gap-3 px-3.5 min-[600px]:px-5">
          <span data-testid="viewer-title" className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-none text-ds-ink">
            {t("dealDetails.doc.contract")}
          </span>
          <button type="button" onClick={handleDownload} disabled={loading || !contractData} className="ds-viewer-download">
            <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M7 1.6v7.2" />
              <path d="M4 6.2 7 9.2l3-3" />
              <path d="M2 11.4h10" />
            </svg>
            <span>{t("actions.downloadPdf")}</span>
          </button>
          <button
            type="button"
            ref={closeRef}
            data-testid="viewer-close"
            onClick={onClose}
            aria-label={t("dealDetails.action.close")}
            className="ds-viewer-close"
          >
            <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" }}>
              <line x1="2" y1="2" x2="12" y2="12" />
              <line x1="12" y1="2" x2="2" y2="12" />
            </svg>
          </button>
        </div>

        <div data-testid="viewer-scroll" className="ds-viewer-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 min-[600px]:px-7 min-[600px]:py-7">
          {loading || !contractData ? (
            <div
              className="overflow-hidden rounded-[13px] border border-ds-rule-edge bg-ds-sheet p-2"
              style={{ maxWidth: "var(--ds-doc-measure)", marginInline: "auto" }}
            >
              {Array.from({ length: 10 }).map((_, idx) => (
                <div key={idx} className="mx-3 my-2 h-4 animate-pulse rounded bg-[rgba(20,36,46,.08)]" style={{ width: "60%" }} />
              ))}
            </div>
          ) : (
            // react-to-print clones ONLY this node into a bare print iframe —
            // it carries along the copied <style> tags but none of this
            // element's real ancestors, so without its own "klargo-public"
            // class (normally inherited from PublicPage's wrapper) every
            // selector in public.css — every one is scoped to
            // ".klargo-public ..." by build-public-css.cjs — fails to match
            // and the printed contract renders unstyled. But .klargo-public
            // is also the rewritten `body` rule (background-color:
            // var(--ds-canvas)) — on screen, giving this node the class
            // paints a second, canvas-coloured box directly behind the
            // document, visibly mismatched against the workspace grey-green
            // behind it. bg-transparent cancels just that one declaration.
            <div ref={printRef} className="klargo-public bg-transparent">
              <ContractDocumentView
                t={t}
                contractData={contractData}
                buyerName={buyerName}
                viewerRole={viewerRole}
                hideSourceNote
                signaturesFooterKey="agreement.signaturesFooterSigned"
                signaturesFooterBold
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
